import React, { useState, useEffect } from 'react';
import CgmModulePageLayout from './layout/CgmModulePageLayout';
import CabinetsAdminShell from './CabinetsAdminShell';
import './CgmFilterToolbar.css';
import './CgmModulePage.css';
import './CabinetsSelectPage.css';
import './UserManagementPage.css';

export default function UserManagementPage({ adminShell = false }) {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showAddForm, setShowAddForm] = useState(false);
    const [showEditForm, setShowEditForm] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        date_of_birth: '',
        email: '',
        phone_number: '',
        password: '',
        confirm_password: '',
        access_level: 'User'
    });
    const [usernameMode, setUsernameMode] = useState('auto'); // 'auto' ou 'manual'
    const [previewUsername, setPreviewUsername] = useState('');
    const [manualUsername, setManualUsername] = useState('');
    const [showAllUsers, setShowAllUsers] = useState(false);

    // Fonction pour générer un username prévisualisé
    const generatePreviewUsername = (firstName, lastName) => {
        const cleanFirstName = firstName ? firstName.trim().replace(/[^a-zA-Z0-9]/g, '').toLowerCase() : '';
        const cleanLastName = lastName ? lastName.trim().replace(/[^a-zA-Z0-9]/g, '').toLowerCase() : '';
        
        // Si aucun des deux n'est rempli, pas de prévisualisation
        if (!cleanFirstName && !cleanLastName) return '';
        
        // Si seulement le prénom : utiliser "caisse" comme nom
        if (cleanFirstName && !cleanLastName) {
            const randomDigits = Math.floor(10 + Math.random() * 90);
            return cleanFirstName + 'caisse' + randomDigits;
        }
        
        // Si seulement le nom : utiliser le nom seul
        if (!cleanFirstName && cleanLastName) {
            const randomDigits = Math.floor(10 + Math.random() * 90);
            return cleanLastName + randomDigits;
        }
        
        // Si les deux : prénom + nom
        const randomDigits = Math.floor(10 + Math.random() * 90);
        return cleanFirstName + cleanLastName + randomDigits;
    };

    // Mettre à jour le username prévisualisé quand first_name ou last_name change
    useEffect(() => {
        if (usernameMode === 'auto' && showAddForm && (formData.first_name || formData.last_name)) {
            const preview = generatePreviewUsername(formData.first_name, formData.last_name);
            setPreviewUsername(preview);
        } else {
            setPreviewUsername('');
        }
    }, [formData.first_name, formData.last_name, usernameMode, showAddForm]);

    const getAuthToken = () => {
        // Désactivé pour les tests (SANS JWT)
        return '';
    };

    useEffect(() => {
        loadUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showAllUsers]);

    const loadUsers = async () => {
        setLoading(true);
        setError('');
        try {
            const token = getAuthToken();
            const response = await fetch('/api/users', {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            const data = await response.json();

            if (data.success) {
                if (!showAllUsers) {
                    // Filtrer les employés (User = caissier)
                    const employes = data.users.filter(user => user.access_level === 'User' || user.role === 'caissier');
                    setUsers(employes);
                } else {
                    setUsers(data.users);
                }
            } else {
                setError(data.error || 'Erreur lors du chargement des utilisateurs');
            }
        } catch (err) {
            setError('Erreur de connexion au serveur');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        // Validation (au moins prénom OU nom, plus email et password)
        if ((!formData.first_name && !formData.last_name) || !formData.email || !formData.password) {
            setError('Au moins le prénom ou le nom, plus l\'email et le mot de passe sont requis');
            setLoading(false);
            return;
        }

        if (formData.password.length < 8) {
            setError('Le mot de passe doit contenir au moins 8 caractères');
            setLoading(false);
            return;
        }

        if (formData.password !== formData.confirm_password) {
            setError('Les mots de passe ne correspondent pas');
            setLoading(false);
            return;
        }

        // Validation username si mode manuel
        if (usernameMode === 'manual' && !manualUsername.trim()) {
            setError('Veuillez saisir un nom d\'utilisateur');
            setLoading(false);
            return;
        }

        if (usernameMode === 'manual' && manualUsername.length < 3) {
            setError('Le nom d\'utilisateur doit contenir au moins 3 caractères');
            setLoading(false);
            return;
        }

        try {
            const token = getAuthToken();
            const response = await fetch('/api/users', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    first_name: formData.first_name,
                    last_name: formData.last_name,
                    date_of_birth: formData.date_of_birth || null,
                    email: formData.email,
                    phone_number: formData.phone_number,
                    password: formData.password,
                    confirm_password: formData.confirm_password,
                    access_level: formData.access_level,
                    username: usernameMode === 'manual' ? manualUsername.trim() : null
                })
            });
            const data = await response.json();
            if (data.success) {
                setSuccess('Utilisateur créé avec succès');
                setFormData({ 
                    first_name: '', 
                    last_name: '', 
                    date_of_birth: '', 
                    email: '', 
                    phone_number: '', 
                    password: '', 
                    confirm_password: '', 
                    access_level: 'User' 
                });
                setShowAddForm(false);
                setTimeout(() => loadUsers(), 100);
            } else {
                setError(data.error || 'Erreur lors de la création de l\'utilisateur');
            }
        } catch (err) {
            setError('Erreur de connexion au serveur');
        } finally {
            setLoading(false);
        }
    };

    const handleEditUser = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        // Validation (au moins prénom OU nom, plus email)
        if ((!formData.first_name || !formData.first_name.trim()) && (!formData.last_name || !formData.last_name.trim())) {
            setError('Au moins le prénom ou le nom est requis');
            setLoading(false);
            return;
        }

        if (!formData.email) {
            setError('Email est requis');
            setLoading(false);
            return;
        }

        if (!formData.username || !formData.username.trim()) {
            setError('Le nom d\'utilisateur est requis');
            setLoading(false);
            return;
        }

        if (formData.username.trim().length < 3) {
            setError('Le nom d\'utilisateur doit contenir au moins 3 caractères');
            setLoading(false);
            return;
        }

        if (formData.password && formData.password.length < 8) {
            setError('Le mot de passe doit contenir au moins 8 caractères');
            setLoading(false);
            return;
        }

        try {
            const token = getAuthToken();
            const response = await fetch(`/api/users/${editingUser.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    username: formData.username.trim(),
                    first_name: formData.first_name,
                    last_name: formData.last_name,
                    date_of_birth: formData.date_of_birth || null,
                    email: formData.email,
                    phone_number: formData.phone_number,
                    password: formData.password || null,
                    access_level: formData.access_level
                })
            });
            const data = await response.json();
            if (data.success) {
                setSuccess('Utilisateur modifié avec succès');
                setFormData({ 
                    first_name: '', 
                    last_name: '', 
                    date_of_birth: '', 
                    email: '', 
                    phone_number: '', 
                    password: '', 
                    confirm_password: '', 
                    access_level: 'User' 
                });
                setEditingUser(null);
                setShowEditForm(false);
                setTimeout(() => loadUsers(), 100);
            } else {
                setError(data.error || 'Erreur lors de la modification de l\'utilisateur');
            }
        } catch (err) {
            setError('Erreur de connexion au serveur');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
            return;
        }
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const token = getAuthToken();
            const response = await fetch(`/api/users/${userId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            const data = await response.json();
            if (data.success) {
                setSuccess('Utilisateur supprimé avec succès');
                setTimeout(() => loadUsers(), 100);
            } else {
                setError(data.error || 'Erreur lors de la suppression de l\'utilisateur');
            }
        } catch (err) {
            setError('Erreur de connexion au serveur');
        } finally {
            setLoading(false);
        }
    };

    const openEditForm = (user) => {
        setEditingUser(user);
        setFormData({ 
            username: user.username || '',
            first_name: user.first_name || '', 
            last_name: user.last_name || '', 
            date_of_birth: user.date_of_birth || '', 
            email: user.email || '', 
            phone_number: user.phone_number || '', 
            password: '', 
            confirm_password: '', 
            access_level: user.access_level || 'User' 
        });
        setShowEditForm(true);
    };

    const formatUserDate = (dateStr) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return '-';
        return d.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatUserDateShort = (dateStr) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return '-';
        return d.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit'
        });
    };

    const cellOrDash = (value) => {
        const text = value != null ? String(value).trim() : '';
        return text || '-';
    };

    const getRoleLabel = (row, short = false) => {
        if (isAdminRole(row)) return short ? 'Adm.' : 'Administrateur';
        return short ? 'Cais.' : 'Caissier';
    };

    const isAdminRole = (row) => (
        row?.access_level === 'SuperAdmin' ||
        row?.access_level === 'Admin' ||
        row?.role === 'admin'
    );

    const resetForm = () => {
        setFormData({ 
            username: '',
            first_name: '', 
            last_name: '', 
            date_of_birth: '', 
            email: '', 
            phone_number: '', 
            password: '', 
            confirm_password: '', 
            access_level: 'User' 
        });
        setUsernameMode('auto');
        setPreviewUsername('');
        setManualUsername('');
        setEditingUser(null);
        setShowAddForm(false);
        setShowEditForm(false);
        setError('');
        setSuccess('');
    };

    const pageStyles = (
            <style jsx global>{`
                body, html { 
                    height: auto !important; 
                    overflow-x: hidden; 
                    overflow-y: auto; 
                    font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif;
                    background: #f8fafc !important;
                }
                
                .user-management-page { 
                    background: transparent;
                    min-height: 0; 
                    padding: 0;
                    max-width: none;
                    margin: 0;
                }
                
                .users-page-head {
                    display: none;
                }
                
                @keyframes slideInUp {
                    from { transform: translateY(30px); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
                
                .action-btn {
                    border-radius: 6px;
                    padding: 5px 12px;
                    font-weight: 600;
                    font-size: 0.75rem;
                    transition: all 0.2s ease;
                    border: none;
                    position: relative;
                    overflow: hidden;
                    margin: 0 3px;
                }
                
                .action-btn-primary {
                    background: linear-gradient(135deg, #0f766e 0%, #115e59 100%);
                    color: white;
                }
                
                .action-btn-success {
                    background: linear-gradient(135deg, #2E7D32 0%, #256528 100%);
                    color: white;
                }
                
                .action-btn:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
                }
                
                .action-btn-primary:hover {
                    background: linear-gradient(135deg, #115e59 0%, #134e4a 100%);
                }
                
                .action-btn-success:hover {
                    background: linear-gradient(135deg, #256528 0%, #1e5e22 100%);
                }

                .users-table-container {
                    background: #ffffff;
                    border-radius: 12px;
                    padding: 0;
                    box-shadow: 0 8px 24px rgba(37, 99, 235, 0.1);
                    border: 1px solid #bfdbfe;
                    overflow: hidden;
                    max-width: 1100px;
                    margin: 0 auto;
                    width: 100%;
                }
                .users-table-body {
                    padding: 0.85rem 1rem 1rem;
                }
                .users-table-bar {
                    background: #0f766e;
                    color: #ffffff;
                    padding: 0.65rem 0.85rem;
                    font-weight: 700;
                    font-size: 0.82rem;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 0.5rem;
                    border-bottom: 1px solid #115e59;
                }
                .users-table-bar-title {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.4rem;
                }
                .users-count-badge {
                    background: rgba(255, 255, 255, 0.2);
                    color: #fff;
                    padding: 0.2rem 0.55rem;
                    border-radius: 999px;
                    font-size: 0.75rem;
                    font-weight: 700;
                    white-space: nowrap;
                }
                
                .table-responsive {
                    width: 100% !important;
                    overflow-x: auto !important;
                    overflow-y: visible;
                    -webkit-overflow-scrolling: touch;
                    scrollbar-width: thin;
                    scrollbar-color: #0f766e #f1f1f1;
                    display: block !important;
                    position: relative;
                }
                
                .table-responsive::-webkit-scrollbar {
                    height: 10px;
                }
                
                .table-responsive::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 5px;
                    margin: 0 5px;
                }
                
                .table-responsive::-webkit-scrollbar-thumb {
                    background: linear-gradient(135deg, #0f766e 0%, #115e59 100%);
                    border-radius: 5px;
                    border: 2px solid #f1f1f1;
                }
                
                .table-responsive::-webkit-scrollbar-thumb:hover {
                    background: linear-gradient(135deg, #115e59 0%, #134e4a 100%);
                }
                
                .modern-table {
                    width: 100% !important;
                    min-width: 960px !important;
                    max-width: 100% !important;
                    border-collapse: separate;
                    border-spacing: 0;
                    border-radius: 6px;
                    overflow: hidden;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
                    border: 1px solid rgba(213, 219, 227, 0.8);
                    table-layout: auto;
                }
                .modern-table .users-cell-email {
                    max-width: 200px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    text-transform: none !important;
                }
                
                .modern-table thead {
                    background: #0f766e;
                }
                
                .modern-table th {
                    color: #ffffff;
                    font-weight: 700;
                    padding: 0.6rem 0.75rem;
                    text-align: left;
                    border-bottom: 1px solid #115e59;
                    border-right: 1px solid rgba(255, 255, 255, 0.15);
                    font-size: 0.8rem;
                    letter-spacing: 0.02em;
                }
                .modern-table th:last-child {
                    text-align: center;
                    border-right: none;
                }
                
                .modern-table tbody tr {
                    background: transparent;
                    transition: all 0.2s ease;
                }
                
                .modern-table tbody tr:nth-child(even) {
                    background: #f8fafc;
                }
                
                .modern-table tbody tr:hover {
                    background: #f0fdfa;
                }
                
                .modern-table td {
                    padding: 0.65rem 0.75rem;
                    text-align: left;
                    vertical-align: middle;
                    border-bottom: 1px solid #e2e8f0;
                    border-right: 1px solid #e2e8f0;
                    font-weight: 500;
                    font-size: 0.88rem;
                    color: #1e293b;
                }
                .modern-table td:last-child {
                    text-align: center;
                }
                
                .modern-table td:last-child {
                    border-right: none;
                }
                
                .modern-table tbody tr:last-child td {
                    border-bottom: none;
                }
                
                .role-badge {
                    padding: 0.2rem 0.55rem;
                    border-radius: 999px;
                    font-weight: 600;
                    font-size: 0.75rem;
                    display: inline-block;
                    white-space: nowrap;
                }
                
                .role-admin {
                    background: #fee2e2;
                    color: #b91c1c;
                    border: 1px solid #fecaca;
                }
                
                .role-caissier {
                    background: #dbeafe;
                    color: #115e59;
                    border: 1px solid #bfdbfe;
                }
                
                .users-row-actions {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                }
                .user-management-page .modern-table .btn-modifier-client {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 34px;
                    height: 34px;
                    background: #ffffff;
                    color: #0f766e;
                    border: 1px solid #dbeafe;
                    border-radius: 8px;
                    padding: 0;
                    cursor: pointer;
                    transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
                }
                .user-management-page .modern-table .btn-modifier-client:hover {
                    background: #f0fdfa;
                    color: #1d4ed8;
                    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.16);
                }
                .user-management-page .modern-table .btn-supprimer-hono {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 34px;
                    height: 34px;
                    background: #ffffff;
                    color: #dc2626;
                    border: 1px solid #fee2e2;
                    border-radius: 8px;
                    padding: 0;
                    cursor: pointer;
                    transition: background 0.2s ease, color 0.2s ease;
                }
                .user-management-page .modern-table .btn-supprimer-hono:hover:not(:disabled) {
                    background: #fef2f2;
                    color: #b91c1c;
                }
                .user-management-page .modern-table .btn-modifier-client svg,
                .user-management-page .modern-table .btn-supprimer-hono svg {
                    width: 18px;
                    height: 18px;
                    display: block;
                }
                
                .modern-alert {
                    border-radius: 6px;
                    border: none;
                    padding: 0.75rem 1rem;
                    margin-bottom: 1rem;
                    font-weight: 500;
                    font-size: 0.85rem;
                }
                
                .modern-alert-success {
                    background: #d4edda;
                    color: #155724;
                    border-left: 3px solid #28a745;
                }
                
                .modern-alert-danger {
                    background: #f8d7da;
                    color: #721c24;
                    border-left: 3px solid #dc3545;
                }
                
                .modern-alert-info {
                    background: #d1ecf1;
                    color: #0c5460;
                    border-left: 3px solid #17a2b8;
                }
                
                .form-container {
                    background: #ffffff;
                    border-radius: 10px;
                    padding: 1rem 1.25rem;
                    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.08);
                    border: 1px solid #bfdbfe;
                    margin-bottom: 1rem;
                }

                /* Forcer le thème light (écrase body.dark-mode) */
                .user-management-page .form-container {
                    background: #ffffff !important;
                    color: #1e293b !important;
                }
                .user-management-page .form-label {
                    color: #475569 !important;
                }
                .user-management-page .form-input,
                .user-management-page .form-container input,
                .user-management-page .form-container select,
                .user-management-page .form-container textarea {
                    background: #ffffff !important;
                    color: #1e293b !important;
                    border-color: #d5dbe3 !important;
                }
                .user-management-page .form-input:focus,
                .user-management-page .form-container input:focus,
                .user-management-page .form-container select:focus {
                    border-color: #0f766e !important;
                    box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.2);
                }
                .user-management-page .users-username-box {
                    background: #f8fafc !important;
                    color: #1e293b !important;
                    border-color: #e2e8f0 !important;
                }
                .user-management-page .users-username-box-title,
                .user-management-page .users-username-radios {
                    color: #475569 !important;
                }
                .user-management-page .users-username-preview {
                    background: #f0fdfa !important;
                    color: #115e59 !important;
                }
                .user-management-page .users-table-container,
                .user-management-page .modern-table {
                    background: #ffffff !important;
                    color: #1e293b !important;
                }

                .users-form-compact {
                    max-width: 680px;
                    margin-left: auto;
                    margin-right: auto;
                    padding: 0.6rem 0.75rem 0.75rem;
                }

                .users-form-compact .form-title {
                    font-size: 0.82rem;
                    padding: 0.4rem 0.55rem;
                    margin: -0.6rem -0.75rem 0.55rem -0.75rem;
                }

                .users-form-compact .form-group {
                    margin-bottom: 0;
                }

                .users-form-compact .form-label {
                    font-size: 0.72rem;
                    margin-bottom: 0.12rem;
                    color: #475569;
                }

                .users-form-compact .form-input {
                    padding: 0.3rem 0.5rem;
                    font-size: 0.78rem;
                    border-radius: 6px;
                    min-height: 30px;
                }

                .users-form-grid {
                    display: grid;
                    grid-template-columns: repeat(3, minmax(0, 1fr));
                    gap: 0.45rem 0.55rem;
                    margin-bottom: 0.5rem;
                }

                .users-form-field-full {
                    grid-column: 1 / -1;
                }

                .users-username-box {
                    background: #f8fafc;
                    padding: 0.45rem 0.55rem;
                    border-radius: 6px;
                    margin-bottom: 0.45rem;
                    border: 1px solid #e2e8f0;
                }

                .users-username-box-title {
                    display: block;
                    font-weight: 600;
                    margin-bottom: 0.35rem;
                    color: #475569;
                    font-size: 0.72rem;
                }

                .users-username-preview {
                    background: #f0fdfa;
                    padding: 0.35rem 0.5rem;
                    border-radius: 6px;
                    margin-bottom: 0.35rem;
                    font-size: 0.72rem;
                    color: #1d4ed8;
                }

                .users-username-radios {
                    display: flex;
                    gap: 0.75rem;
                    flex-wrap: wrap;
                    margin-bottom: 0.35rem;
                    font-size: 0.72rem;
                }

                .users-username-radios label {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.25rem;
                    margin: 0;
                    cursor: pointer;
                }

                .users-form-compact .form-buttons {
                    margin-top: 0.5rem;
                    gap: 0.4rem;
                }

                .users-form-compact .form-btn {
                    padding: 0.35rem 0.85rem;
                    font-size: 0.75rem;
                    min-width: 88px;
                }
                
                .form-title {
                    background: #0f766e;
                    color: #ffffff;
                    font-weight: 700;
                    font-size: 0.95rem;
                    padding: 0.5rem 0.75rem;
                    margin: -1rem -1.25rem 1rem -1.25rem;
                    text-align: center;
                    border-radius: 8px 8px 0 0;
                }
                
                .form-group {
                    margin-bottom: 1rem;
                }
                
                .form-label {
                    color: #2c3e50;
                    font-weight: 600;
                    font-size: 0.8rem;
                    margin-bottom: 0.3rem;
                    display: block;
                }
                
                .form-input {
                    width: 100%;
                    border: 1px solid #d5dbe3;
                    border-radius: 4px;
                    padding: 6px 10px;
                    font-size: 0.85rem;
                    transition: all 0.2s ease;
                    background: #ffffff;
                }
                
                .form-input:focus {
                    outline: none;
                    border-color: #0f766e;
                    box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.2);
                }
                
                .form-buttons {
                    display: flex;
                    gap: 0.5rem;
                    justify-content: center;
                    margin-top: 1rem;
                }
                
                .form-btn {
                    border-radius: 4px;
                    padding: 6px 16px;
                    font-weight: 600;
                    font-size: 0.8rem;
                    transition: all 0.2s ease;
                    border: none;
                    min-width: 100px;
                    cursor: pointer;
                }
                
                .form-btn-secondary {
                    background: linear-gradient(135deg, #6c757d 0%, #495057 100%);
                    color: white;
                }
                
                .form-btn-primary {
                    background: linear-gradient(180deg, #0f766e 0%, #115e59 100%);
                    color: white;
                }
                
                .form-btn:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
                }
                
                .form-btn-primary:hover {
                    background: linear-gradient(180deg, #115e59 0%, #134e4a 100%);
                }
                
                .form-btn-secondary:hover {
                    background: linear-gradient(135deg, #5a6268 0%, #343a40 100%);
                }
                
                .form-btn:disabled {
                    opacity: 0.7;
                    transform: none;
                    cursor: not-allowed;
                }
                
                .empty-state {
                    text-align: center;
                    padding: 4rem 2rem;
                    color: #6c757d;
                }
                
                .empty-state i {
                    font-size: 4rem;
                    margin-bottom: 1rem;
                    opacity: 0.5;
                }
                
                .empty-state h5 {
                    margin-bottom: 1rem;
                    font-weight: 600;
                }
                
                .empty-state p {
                    margin-bottom: 2rem;
                    opacity: 0.8;
                }
                
                .modern-spinner {
                    width: 20px;
                    height: 20px;
                    border: 2px solid #f3f3f3;
                    border-top: 2px solid #0f766e;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    display: inline-block;
                    margin-right: 8px;
                }
                
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                .empty-state {
                    text-align: center;
                    padding: 3rem 2rem;
                    color: #6c757d;
                }
                
                .empty-state i {
                    font-size: 3rem;
                    margin-bottom: 1rem;
                    opacity: 0.5;
                }
                
                .empty-state h5 {
                    margin-bottom: 1rem;
                    font-weight: 600;
                }
                
                .empty-state p {
                    margin-bottom: 2rem;
                    opacity: 0.8;
                }
                
                .users-th-short,
                .users-date-short,
                .role-label-short {
                    display: none;
                }

                @media (max-width: 768px) {
                    .users-form-grid {
                        grid-template-columns: repeat(2, minmax(0, 1fr));
                        gap: 0.4rem 0.45rem;
                    }
                    .users-form-compact {
                        max-width: 100%;
                        padding: 0.5rem 0.45rem 0.65rem;
                    }
                    .users-form-compact .form-title {
                        margin: -0.5rem -0.45rem 0.45rem -0.45rem;
                    }
                    .form-buttons {
                        flex-direction: column;
                    }
                    .users-toolbar {
                        flex-direction: column;
                        align-items: stretch;
                    }
                    .users-toolbar-btn {
                        width: 100%;
                    }
                    .user-management-page {
                        padding: 0.65rem 0.35rem 1rem;
                    }
                    .users-table-body {
                        padding: 0.5rem 0.25rem;
                    }
                    .users-table-mobile-fit {
                        overflow-x: hidden !important;
                    }
                    .users-table-mobile-fit .modern-table {
                        min-width: 0 !important;
                        width: 100% !important;
                        table-layout: fixed;
                    }
                    .users-table-mobile-fit .modern-table th,
                    .users-table-mobile-fit .modern-table td {
                        padding: 0.28rem 0.12rem;
                        font-size: 0.58rem;
                        line-height: 1.2;
                        word-break: break-word;
                        text-transform: none !important;
                    }
                    .users-table-mobile-fit .modern-table th {
                        font-size: 0.52rem;
                        font-weight: 700;
                        vertical-align: middle;
                    }
                    .users-th-long,
                    .users-date-long,
                    .role-label-long {
                        display: none;
                    }
                    .users-th-short,
                    .users-date-short,
                    .role-label-short {
                        display: inline;
                    }
                    .users-table-mobile-fit .modern-table .users-cell-email,
                    .users-table-mobile-fit .modern-table .users-cell-phone {
                        max-width: none;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }
                    .users-table-mobile-fit .role-badge {
                        font-size: 0.48rem;
                        padding: 0.1rem 0.25rem;
                    }
                    .users-table-mobile-fit .users-row-actions {
                        gap: 2px;
                    }
                    .user-management-page .users-table-mobile-fit .modern-table .btn-modifier-client,
                    .user-management-page .users-table-mobile-fit .modern-table .btn-supprimer-hono {
                        width: 22px;
                        height: 22px;
                        border-radius: 4px;
                    }
                    .user-management-page .users-table-mobile-fit .modern-table .btn-modifier-client svg,
                    .user-management-page .users-table-mobile-fit .modern-table .btn-supprimer-hono svg {
                        width: 11px !important;
                        height: 11px !important;
                    }
                    .users-table-mobile-fit .modern-table th:nth-child(1),
                    .users-table-mobile-fit .modern-table td:nth-child(1) { width: 9%; }
                    .users-table-mobile-fit .modern-table th:nth-child(2),
                    .users-table-mobile-fit .modern-table td:nth-child(2) { width: 9%; }
                    .users-table-mobile-fit .modern-table th:nth-child(3),
                    .users-table-mobile-fit .modern-table td:nth-child(3) { width: 20%; }
                    .users-table-mobile-fit .modern-table th:nth-child(4),
                    .users-table-mobile-fit .modern-table td:nth-child(4) { width: 14%; }
                    .users-table-mobile-fit .modern-table th:nth-child(5),
                    .users-table-mobile-fit .modern-table td:nth-child(5) { width: 11%; }
                    .users-table-mobile-fit .modern-table th:nth-child(6),
                    .users-table-mobile-fit .modern-table td:nth-child(6) { width: 9%; }
                    .users-table-mobile-fit .modern-table th:nth-child(7),
                    .users-table-mobile-fit .modern-table td:nth-child(7) { width: 11%; }
                    .users-table-mobile-fit .modern-table th:nth-child(8),
                    .users-table-mobile-fit .modern-table td:nth-child(8) { width: 17%; }
                }

                @media (max-width: 520px) {
                    .users-form-grid {
                        grid-template-columns: 1fr;
                        gap: 0.35rem;
                    }
                    .users-form-compact .form-input {
                        min-height: 28px;
                    }
                    .users-username-radios {
                        flex-direction: column;
                        gap: 0.35rem;
                    }
                }
            `}</style>
    );

    const pageBody = (
        <>
            {pageStyles}

            <div className="cgm-module-table-toolbar">
                <button
                    type="button"
                    className="cgm-toolbar-btn cgm-toolbar-btn--reset"
                    onClick={() => setShowAllUsers(!showAllUsers)}
                    title={showAllUsers ? 'Voir employés seulement' : 'Voir tous les utilisateurs'}
                >
                    <i className={`fas ${showAllUsers ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden="true" />
                    {showAllUsers ? 'Voir employés seulement' : 'Voir tous les utilisateurs'}
                </button>
                <button
                    type="button"
                    className="cgm-toolbar-btn cgm-toolbar-btn--add"
                    onClick={() => setShowAddForm(true)}
                    title="Ajouter un utilisateur"
                >
                    <i className="fas fa-plus" aria-hidden="true" />
                    Ajouter un utilisateur
                </button>
            </div>

            <div className="users-table-container cgm-module-table-card">
                <div className="users-table-bar">
                    <span className="users-table-bar-title">
                        <i className="fas fa-list" aria-hidden="true" />
                        Liste des utilisateurs
                    </span>
                    {!loading && (
                        <span className="users-count-badge">
                            {users.length} utilisateur{users.length !== 1 ? 's' : ''}
                        </span>
                    )}
                </div>

                <div className="users-table-body">
                {error && (
                    <div className="modern-alert modern-alert-danger">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        {error}
                    </div>
                )}
                {success && (
                    <div className="modern-alert modern-alert-success">
                        <i className="fas fa-check-circle me-2"></i>
                        {success}
                    </div>
                )}

                {showAddForm && (
                    <div className="form-container users-form-compact">
                        <h3 className="form-title">
                            <i className="fas fa-user-plus me-2"></i>Ajouter un utilisateur
                        </h3>
                        <form onSubmit={handleAddUser}>
                            <div className="users-form-grid">
                                <div className="form-group">
                                    <label className="form-label">Prénom</label>
                                    <input type="text" className="form-input" name="first_name" value={formData.first_name} onChange={handleInputChange} />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Nom</label>
                                    <input type="text" className="form-input" name="last_name" value={formData.last_name} onChange={handleInputChange} />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Date de naissance</label>
                                    <input type="date" className="form-input" name="date_of_birth" value={formData.date_of_birth} onChange={handleInputChange} />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Email *</label>
                                    <input type="email" className="form-input" name="email" value={formData.email} onChange={handleInputChange} required />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Téléphone</label>
                                    <input type="text" className="form-input" name="phone_number" value={formData.phone_number} onChange={handleInputChange} />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Niveau d&apos;accès *</label>
                                    <select className="form-input" name="access_level" value={formData.access_level} onChange={handleInputChange} required>
                                        <option value="User">User (Caissier)</option>
                                        <option value="Admin">Admin</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Mot de passe *</label>
                                    <input type="password" className="form-input" name="password" value={formData.password} onChange={handleInputChange} required minLength={8} title="Minimum 8 caractères" />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Confirmer mot de passe *</label>
                                    <input type="password" className="form-input" name="confirm_password" value={formData.confirm_password} onChange={handleInputChange} required minLength={8} />
                                </div>
                            </div>

                            <div className="users-username-box">
                                <span className="users-username-box-title">
                                    <i className="fas fa-user-circle me-1" aria-hidden="true" />
                                    Nom d&apos;utilisateur
                                </span>
                                {usernameMode === 'auto' && previewUsername && (
                                    <div className="users-username-preview">
                                        <i className="fas fa-eye me-1" aria-hidden="true" />
                                        Prévisualisation : <strong>{previewUsername}</strong>
                                    </div>
                                )}
                                <div className="users-username-radios">
                                    <label>
                                        <input
                                            type="radio"
                                            name="usernameMode"
                                            value="auto"
                                            checked={usernameMode === 'auto'}
                                            onChange={(e) => {
                                                setUsernameMode(e.target.value);
                                                setManualUsername('');
                                            }}
                                        />
                                        Auto
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            name="usernameMode"
                                            value="manual"
                                            checked={usernameMode === 'manual'}
                                            onChange={(e) => setUsernameMode(e.target.value)}
                                        />
                                        Manuel
                                    </label>
                                </div>
                                {usernameMode === 'manual' && (
                                    <div className="form-group">
                                        <input
                                            type="text"
                                            className="form-input"
                                            value={manualUsername}
                                            onChange={(e) => setManualUsername(e.target.value)}
                                            placeholder="Nom d'utilisateur *"
                                            required={usernameMode === 'manual'}
                                            minLength={3}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="form-buttons">
                                <button type="submit" className="form-btn form-btn-primary" disabled={loading}>
                                    {loading ? 'Création...' : 'Créer'}
                                </button>
                                <button type="button" className="form-btn form-btn-secondary" onClick={resetForm}>Annuler</button>
                            </div>
                        </form>
                    </div>
                )}

                {showEditForm && (
                    <div className="form-container users-form-compact">
                        <h3 className="form-title">
                            <i className="fas fa-user-edit me-2"></i>Modifier l&apos;utilisateur
                        </h3>
                        <form onSubmit={handleEditUser}>
                            <div className="users-form-grid">
                                <div className="form-group users-form-field-full">
                                    <label className="form-label">Nom d&apos;utilisateur *</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        name="username"
                                        value={formData.username || ''}
                                        onChange={handleInputChange}
                                        required
                                        minLength={3}
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Prénom</label>
                                    <input type="text" className="form-input" name="first_name" value={formData.first_name} onChange={handleInputChange} />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Nom</label>
                                    <input type="text" className="form-input" name="last_name" value={formData.last_name} onChange={handleInputChange} />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Date de naissance</label>
                                    <input type="date" className="form-input" name="date_of_birth" value={formData.date_of_birth} onChange={handleInputChange} />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Email *</label>
                                    <input type="email" className="form-input" name="email" value={formData.email} onChange={handleInputChange} required />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Téléphone</label>
                                    <input type="text" className="form-input" name="phone_number" value={formData.phone_number} onChange={handleInputChange} />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Nouveau mot de passe</label>
                                    <input type="password" className="form-input" name="password" value={formData.password} onChange={handleInputChange} minLength={8} placeholder="Vide = inchangé" />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Niveau d&apos;accès *</label>
                                    <select className="form-input" name="access_level" value={formData.access_level} onChange={handleInputChange} required>
                                        <option value="User">User (Caissier)</option>
                                        <option value="Admin">Admin</option>
                                    </select>
                                </div>
                            </div>
                            <div className="form-buttons">
                                <button type="submit" className="form-btn form-btn-primary" disabled={loading}>
                                    {loading ? 'Modification...' : 'Modifier'}
                                </button>
                                <button type="button" className="form-btn form-btn-secondary" onClick={resetForm}>Annuler</button>
                            </div>
                        </form>
                    </div>
                )}

                {loading ? (
                    <div className="text-center py-5">
                        <div className="modern-spinner"></div>
                        <p className="mt-3">Chargement des utilisateurs...</p>
                    </div>
                ) : users.length === 0 ? (
                    <div className="empty-state">
                        <i className="fas fa-users"></i>
                        <h5>{!showAllUsers ? 'Aucun employé trouvé' : 'Aucun utilisateur trouvé'}</h5>
                        <p>{!showAllUsers ? 'Il n\'y a pas encore d\'employés dans le système.' : 'Il n\'y a pas encore d\'utilisateurs dans le système.'}</p>
                        <button type="button" className="cgm-toolbar-btn cgm-toolbar-btn--add" onClick={() => setShowAddForm(true)}>
                            <i className="fas fa-plus" aria-hidden="true"></i>
                            Ajouter le premier utilisateur
                        </button>
                    </div>
                ) : adminShell ? (
                    <div className="um-list" role="list">
                        {users.map((userRow) => {
                            const name = [userRow.first_name, userRow.last_name].filter(Boolean).join(' ') || userRow.username || 'User';
                            const parts = String(name).trim().split(/\s+/);
                            const ini =
                                parts.length >= 2
                                    ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
                                    : String(name).slice(0, 2).toUpperCase();
                            const isAdm = isAdminRole(userRow);
                            return (
                                <article
                                    key={userRow.id}
                                    role="listitem"
                                    className={`um-card${isAdm ? ' um-card--admin' : ''}`}
                                >
                                    <div className="um-card__main">
                                        <div className={`um-card__avatar${isAdm ? ' is-admin' : ''}`} aria-hidden="true">
                                            {ini}
                                        </div>
                                        <div className="um-card__id">
                                            <strong>{name}</strong>
                                            <span className="um-card__mail">{cellOrDash(userRow.email)}</span>
                                            <span className={`um-role ${isAdm ? 'um-role--admin' : 'um-role--user'}`}>
                                                {getRoleLabel(userRow)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="um-card__meta">
                                        <div>
                                            <span className="um-card__label">Téléphone</span>
                                            <span>{cellOrDash(userRow.phone_number)}</span>
                                        </div>
                                        <div>
                                            <span className="um-card__label">Naissance</span>
                                            <span>{formatUserDate(userRow.date_of_birth)}</span>
                                        </div>
                                        <div>
                                            <span className="um-card__label">Créé le</span>
                                            <span>{formatUserDate(userRow.date_creation || userRow.created_at)}</span>
                                        </div>
                                    </div>
                                    <div className="um-card__actions">
                                        <button
                                            type="button"
                                            className="um-act um-act--edit"
                                            onClick={() => openEditForm(userRow)}
                                            title="Modifier"
                                        >
                                            Modifier
                                        </button>
                                        <button
                                            type="button"
                                            className="um-act um-act--del"
                                            onClick={() => handleDeleteUser(userRow.id)}
                                            title="Supprimer"
                                        >
                                            Supprimer
                                        </button>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                ) : (
                    <div className="table-responsive users-table-mobile-fit">
                        <table className="modern-table">
                            <thead>
                                <tr>
                                    <th>
                                        <span className="users-th-long">Prénom</span>
                                        <span className="users-th-short">Prén.</span>
                                    </th>
                                    <th>Nom</th>
                                    <th>
                                        <span className="users-th-long">Email</span>
                                        <span className="users-th-short">Mail</span>
                                    </th>
                                    <th>
                                        <span className="users-th-long">Téléphone</span>
                                        <span className="users-th-short">Tél.</span>
                                    </th>
                                    <th>
                                        <span className="users-th-long">Date de naissance</span>
                                        <span className="users-th-short">Naiss.</span>
                                    </th>
                                    <th>Rôle</th>
                                    <th>
                                        <span className="users-th-long">Date création</span>
                                        <span className="users-th-short">Créat.</span>
                                    </th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((userRow) => (
                                    <tr key={userRow.id}>
                                        <td>{cellOrDash(userRow.first_name)}</td>
                                        <td>{cellOrDash(userRow.last_name)}</td>
                                        <td className="users-cell-email" title={userRow.email || ''}>{cellOrDash(userRow.email)}</td>
                                        <td className="users-cell-phone" title={userRow.phone_number || ''}>{cellOrDash(userRow.phone_number)}</td>
                                        <td>
                                            <span className="users-date-long">{formatUserDate(userRow.date_of_birth)}</span>
                                            <span className="users-date-short">{formatUserDateShort(userRow.date_of_birth)}</span>
                                        </td>
                                        <td>
                                            <span className={`role-badge ${isAdminRole(userRow) ? 'role-admin' : 'role-caissier'}`}>
                                                <span className="role-label-long">{getRoleLabel(userRow)}</span>
                                                <span className="role-label-short">{getRoleLabel(userRow, true)}</span>
                                            </span>
                                        </td>
                                        <td>
                                            <span className="users-date-long">{formatUserDate(userRow.date_creation || userRow.created_at)}</span>
                                            <span className="users-date-short">{formatUserDateShort(userRow.date_creation || userRow.created_at)}</span>
                                        </td>
                                        <td>
                                            <div className="users-row-actions">
                                                <button
                                                    type="button"
                                                    className="btn-modifier-client"
                                                    onClick={() => openEditForm(userRow)}
                                                    title="Modifier"
                                                    aria-label="Modifier"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                        <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm14.71-9.04c.39-.39.39-1.02 0-1.41l-2.5-2.5a.9959.9959 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.99-1.67z" />
                                                    </svg>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn-supprimer-hono"
                                                    onClick={() => handleDeleteUser(userRow.id)}
                                                    title="Supprimer"
                                                    aria-label="Supprimer"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                        <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                </div>
            </div>
        </>
    );

    if (adminShell) {
        return (
            <CabinetsAdminShell>
                <div className="user-management-page um-modern">
                    <header className="um-hero">
                        <div className="um-hero__glow" aria-hidden="true" />
                        <div className="um-hero__text">
                            <div className="um-hero__eyebrow">Administration</div>
                            <h1>Gérer les utilisateurs</h1>
                            <p>
                                Créez et gérez les comptes employés et admins. Les points
                                d&apos;accès par cabinet se règlent dans « Point d&apos;accès ».
                            </p>
                        </div>
                        <div className="um-hero__stats">
                            <span className="um-hero__stat">
                                <strong>{users.length}</strong>
                                {' '}utilisateur{users.length !== 1 ? 's' : ''}
                            </span>
                        </div>
                    </header>
                    {pageBody}
                </div>
            </CabinetsAdminShell>
        );
    }

    return (
        <CgmModulePageLayout
            crumb="Administration · Comptes"
            title="Gestion des utilisateurs"
            backTo="/"
            className="user-management-page"
        >
            {pageBody}
        </CgmModulePageLayout>
    );
}


